import { createContext, useContext, useReducer, useEffect } from 'react';
import { signInWithPopup } from 'firebase/auth';
import { auth, authProvider, pcServiceUrl } from '../config';
import { useLocalStorageState } from '../hooks/useLocalStorageState';

const AuthContext = createContext();

const initialState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  mapsApiKey: null,
};

function reducer(state, action) {
  switch (action.type) {
    case 'login/google':
      //console.log('AuthContext(reducer): login/google');
      return {
        ...state,
        user: {
          uid: action.payload.uid,
          name: action.payload.displayName,
          email: action.payload.email,
          photoURL: action.payload.photoURL,
        },
        isAuthenticated: true,
        isLoading: false,
        mapsApiKey: action.payload.mapsApiKey,
      };
    case 'login/restore':
      //console.log('AuthContext(reducer): login/restore success');
      return {
        ...state,
        user: action.payload.user,
        isAuthenticated: true,
        isLoading: false,
        mapsApiKey: action.payload.mapsApiKey,
      };
    case 'logout':
      //console.log('AuthContext(reducer): logout');
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        mapsApiKey: null,
      };
    case 'auth/check-complete':
      return { ...state, isLoading: false };
    default:
      throw new Error('Unknown action');
  }
}

function AuthProvider({ children }) {
  const [{ user, isAuthenticated, isLoading, mapsApiKey }, dispatch] =
    useReducer(reducer, initialState);
  const [savedToken, setToken] = useLocalStorageState(null, 'token');
  const [savedUser, setUser] = useLocalStorageState(null, 'user');

  // Check localStorage on mount and restore session if valid
  useEffect(() => {
    const checkStoredAuth = async () => {
      if (savedToken && savedUser) {
        try {
          // First try to get a fresh token
          let freshToken = null;
          try {
            if (auth.currentUser) {
              freshToken = await auth.currentUser.getIdToken(true);
            } else {
              // If no current user, start with saved token
              freshToken = savedToken;
            }
          } catch (tokenError) {
            console.error('Error refreshing token:', tokenError);
            // Continue with saved token as fallback
            freshToken = savedToken;
          }

          // Verify the token
          //console.log('Verifying User...');
          const response = await fetch(`${pcServiceUrl}/api/v1/user/verify`, {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${freshToken}`,
            },
          });

          if (response.ok) {
            const data = await response.json();

            // Update stored token if we got a fresh one
            if (freshToken !== savedToken) {
              setToken(freshToken);
            }

            // Create/update user entry with the token
            const userEntry = await createOrUpdateUserEntry(
              savedUser,
              freshToken
            );

            //console.log('login/restore -> userEntry:', userEntry);

            dispatch({
              type: 'login/restore',
              payload: {
                user: {
                  uid: savedUser.uid,
                  name: savedUser.displayName,
                  email: savedUser.email,
                  photoURL: savedUser.photoURL,
                  role: userEntry.role,
                },
                mapsApiKey: data.mapsKey,
              },
            });
          } else {
            console.error('Token verification failed, logging out');
            setToken(null);
            setUser(null);
            dispatch({ type: 'logout' });
          }
        } catch (error) {
          console.error('Error during auth check:', error);
          setToken(null);
          setUser(null);
          dispatch({ type: 'logout' });
        }
      } else {
        dispatch({ type: 'auth/check-complete' });
      }
    };

    checkStoredAuth();
  }, [savedToken, savedUser, setToken, setUser]);

  const createOrUpdateUserEntry = async (user, token = null) => {
    try {
      //console.log('Updating user entry...');

      let idToken = token;

      // If no token provided, try to get a fresh one
      if (!idToken && auth.currentUser) {
        try {
          idToken = await auth.currentUser.getIdToken(true);
        } catch (tokenError) {
          console.error('Failed to get fresh token:', tokenError);
          // Try to get current token as fallback
          idToken = await auth.currentUser.getIdToken();
        }
      }

      if (!idToken) {
        console.error('No token available for user entry update');
        return;
      }

      // Ensure token is properly formatted
      const formattedToken = idToken.startsWith('Bearer ')
        ? idToken
        : `Bearer ${idToken}`;

      const response = await fetch(`${pcServiceUrl}/api/v1/user/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: formattedToken,
        },
        body: JSON.stringify({
          userId: user.uid,
          lastSeen: Date.now(),
          firebaseName: user.displayName,
          email: user.email,
          photoUrl: user.photoURL,
          os: 'web',
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update user entry: ${errorText}`);
      }

      const data = await response.json();
      //console.log('createOrUpdateUserEntry response:', data);
      return data;
    } catch (error) {
      console.error('Error creating/updating user entry:', error);
      // Don't throw the error to prevent breaking the auth flow
    }
  };

  async function loginWithGoogle() {
    try {
      const result = await signInWithPopup(auth, authProvider);
      const user = result.user;

      // Get the correct Firebase ID token
      const idToken = await user.getIdToken(true); // Force refresh token

      // Verify token and get maps API key
      const verifyResponse = await fetch(`${pcServiceUrl}/api/v1/user/verify`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });

      if (!verifyResponse.ok) {
        throw new Error('Failed to verify user');
      }

      const verifyData = await verifyResponse.json();

      // Create/update user entry
      const userEntry = await createOrUpdateUserEntry(user);
      console.log('loginWithGoogle -> userEntry:', userEntry);

      setToken(idToken); // Store the correct token
      setUser(user);
      dispatch({
        type: 'login/google',
        payload: {
          ...user,
          mapsApiKey: verifyData.mapsKey,
          role: userEntry.role,
        },
      });
    } catch (error) {
      console.error('Google login error:', error);
      throw error;
    }
  }

  function logout() {
    setToken((_) => {
      return null;
    });
    setUser(null);
    dispatch({ type: 'logout' });
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        isLoading,
        mapsApiKey,
        loginWithGoogle,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined)
    throw new Error('AuthContext was used outside AuthProvider');
  return context;
}

export { AuthProvider, useAuth };
